import { useEffect, useState } from 'react';

/**
 * Light / dark toggle.
 *
 * Three states rather than two: light, dark, or follow the device.
 * Following is the default, because a reader who has set their phone
 * to dark at bedtime has already told us what they want - and an
 * explicit choice must still win, so the attribute on <html> beats the
 * media query in theme.css.
 *
 * The chosen theme is applied in index.html before the app renders, so
 * a dark reader never gets a white flash first. This component only
 * changes it afterwards.
 */

const KEY = 'cybercub.theme';

export const readTheme = () => {
  try {
    const saved = localStorage.getItem(KEY);
    return saved === 'light' || saved === 'dark' ? saved : 'system';
  } catch {
    return 'system';
  }
};

/** Puts the choice on <html>, or removes it to follow the device. */
export function applyTheme(theme) {
  const root = document.documentElement;
  if (theme === 'system') root.removeAttribute('data-theme');
  else root.setAttribute('data-theme', theme);
  try {
    if (theme === 'system') localStorage.removeItem(KEY);
    else localStorage.setItem(KEY, theme);
  } catch {
    /* private window - the theme still applies for this visit */
  }
}

const NEXT = { system: 'light', light: 'dark', dark: 'system' };

const FACE = {
  system: { icon: '🌗', label: 'Theme: follows your device' },
  light: { icon: '☀️', label: 'Theme: light' },
  dark: { icon: '🌙', label: 'Theme: dark' },
};

export default function ThemeToggle({ className = '' }) {
  const [theme, setTheme] = useState(readTheme);

  useEffect(() => {
    applyTheme(theme);
  }, [theme]);

  const face = FACE[theme];

  return (
    <button
      type="button"
      className={`theme-toggle ${className}`}
      onClick={() => setTheme((t) => NEXT[t])}
      title={`${face.label} — tap to change`}
      aria-label={`${face.label}. Tap to switch to ${FACE[NEXT[theme]].label.replace('Theme: ', '')}.`}
    >
      <span aria-hidden="true">{face.icon}</span>
    </button>
  );
}
